import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import LikesDrawer from "@/components/common/likes-drawer/likes-drawer";
import type {
    LikesDrawerContextValue,
    LikesDrawerState,
    OpenLikesDrawerParams,
} from "@/components/common/likes-drawer/likes-drawer.types";

const LikesDrawerContext = createContext<LikesDrawerContextValue | null>(null);

/**
 * Provides global likes-drawer state for feed and scoreboard surfaces.
 */
export function LikesDrawerProvider({ children }: { children: ReactNode }): ReactElement {
    const [drawerState, setDrawerState] = useState<LikesDrawerState | null>(null);
    const openLikesDrawer = useCallback((params: OpenLikesDrawerParams): void => {
        if (params.likeCount <= 0) {
            return;
        }
        setDrawerState({
            ...params,
            isOpen: true,
        });
    }, []);
    const closeLikesDrawer = useCallback((): void => {
        setDrawerState((current) => (current ? { ...current, isOpen: false } : null));
    }, []);
    const contextValue = useMemo(
        (): LikesDrawerContextValue => ({
            openLikesDrawer,
        }),
        [openLikesDrawer],
    );
    return (
        <LikesDrawerContext.Provider value={contextValue}>
            {children}
            <LikesDrawer state={drawerState} onClose={closeLikesDrawer} />
        </LikesDrawerContext.Provider>
    );
}

/**
 * Opens the global likes drawer; must be used within LikesDrawerProvider.
 */
export function useLikesDrawer(): LikesDrawerContextValue {
    const context = useContext(LikesDrawerContext);
    if (!context) {
        throw new Error("useLikesDrawer must be used within LikesDrawerProvider");
    }
    return context;
}
